import { request, type RequestOptions } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import { logger } from '../../utils/logger'
import { projectsApi } from './projects'
import type { Model, ModelCreate, CatalogCreatorOption } from './types'

/** Pricing interval row from TAGP backend */
export interface FoundationModelPricingInterval {
  id: string
  foundation_model_id: string
  effective_from: string
  effective_until: string | null
  input_usd_per_million: string
  output_usd_per_million: string
  pricing_unit?: string
  source: string
  provider_adapter?: string | null
  harvest_batch_id?: string | null
  raw_snapshot_ref?: string | null
  created_at?: number | null
  updated_at?: number | null
  created_by?: string | null
  updated_by?: string | null
  /** True when this row is the catalog “latest” rate (matches top summary). */
  is_current?: boolean
}

export interface FoundationModelPricingListResponse {
  latest: FoundationModelPricingInterval | null
  history: FoundationModelPricingInterval[]
  history_total?: number | null
  history_page?: number | null
  history_page_size?: number | null
}

export interface ProviderPricingRankItem {
  provider: string
  model_count: number
  models_with_open_pricing: number
  min_input_usd_per_million: string | null
  min_output_usd_per_million: string | null
}

export interface PricingHarvestResponse {
  harvest_batch_id: string
  counters: Record<string, number>
  errors: Array<{ model_id: string; error: string }>
  completed_at: string
}

function cleanModelDisplayName(displayName: string | null | undefined, modelId: string | null | undefined): string {
  if (!displayName) return displayName || '';

  // 1. Check for exact identical halves (e.g. "Azure OpenAI GPT-4o Mini Azure OpenAI GPT-4o Mini")
  const mid = Math.floor(displayName.length / 2);
  if (displayName.length % 2 === 1 && displayName[mid] === ' ') {
    if (displayName.substring(0, mid) === displayName.substring(mid + 1)) {
      return displayName.substring(0, mid);
    }
  }

  // 2. Check if it ends with modelId and the remaining STILL contains modelId
  // (e.g. "Google Gemini 3 Flash Preview Gemini 3 Flash Preview" where modelId="Gemini 3 Flash Preview")
  if (modelId && displayName.endsWith(" " + modelId)) {
    const remaining = displayName.slice(0, -(modelId.length + 1)).trim();
    if (remaining.includes(modelId) || remaining === modelId) {
      return remaining;
    }
  }

  return displayName;
}

// Models API (Foundation Models - user-registered)
type ModelsRequestOptions = Pick<RequestOptions, 'signal' | 'retryOn401' | 'tenantId'>

export const modelsApi = {
  /** Unpaginated list for dropdowns/selectors (e.g. agent node model picker). */
  listAll: (params?: {
    provider?: string
    model_type?: string
    project_id?: string
    omit_linked_for_project?: string
    only_approved_linked_for_project?: string
    include_project_membership_for?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Model[]; total: number }>(`/models/all${query}`, { signal })
      .then((res) => ({
        ...res,
        items: (res.items || []).map(m => ({ ...m, display_name: cleanModelDisplayName(m.display_name, m.model_id) }))
      }))
  },

  list: (params?: {
    search?: string
    page?: number
    page_size?: number
    provider?: string
    model_type?: string
    project_id?: string
    sort?: string
    include_latest_pricing?: boolean
    omit_linked_for_project?: string
    only_approved_linked_for_project?: string
    include_project_membership_for?: string
    /** Comma-separated user UUIDs to filter by creator(s) */
    creator_id?: string
    /** Catalog activation: active | inactive (maps to is_active) */
    activation?: string
    include_access_project_lists?: boolean
    include_catalog_filter_options?: boolean
    is_favourite?: boolean
    /** Platform admin cross-tenant catalog */
    scope?: 'all'
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    const url = `/models${query}`
    logger.debug('modelsApi.list: Making request', { url, params: queryParams })
    return request<{ items: Model[]; total: number; catalog_creators?: CatalogCreatorOption[] }>(url, {
      signal,
    })
      .then((response) => {
        const cleanedItems = (response.items || []).map(m => ({ ...m, display_name: cleanModelDisplayName(m.display_name, m.model_id) }))
        logger.debug('modelsApi.list: Response received', {
          itemsCount: cleanedItems.length || 0,
          total: response.total,
          firstItem: cleanedItems[0],
        })
        return { ...response, items: cleanedItems }
      })
      .catch((error) => {
        logger.error('modelsApi.list: Request failed', error, { url, params: queryParams })
        throw error
      })
  },

  get: (id: string, options?: ModelsRequestOptions) =>
    request<Model>(`/models/${id}`, {
      signal: options?.signal,
      tenantId: options?.tenantId,
    }).then(m => ({ ...m, display_name: cleanModelDisplayName(m.display_name, m.model_id) })),

  create: (data: ModelCreate, options?: ModelsRequestOptions) =>
    request<Model>('/models', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  update: (id: string, data: Partial<ModelCreate>, options?: ModelsRequestOptions) =>
    request<Model>(`/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  delete: (id: string, options?: ModelsRequestOptions) =>
    request<void>(`/models/${id}`, {
      method: 'DELETE',
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  bulkDelete: (modelIds: string[], options?: ModelsRequestOptions & { force?: boolean }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/models/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ model_ids: modelIds }),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  // Get provider templates for UI dropdowns
  getProviders: (options?: { signal?: AbortSignal }) =>
    request<
      Record<
        string,
        {
          display_name: string
          model_templates?: Array<{
            model_id: string
            display_name: string
            model_type?: 'llm' | 'embedding' | 'tts' | 'stt' | 'image' | 'reranker' | 'audio' | 'video'
            default_params?: Record<string, unknown>
          }>
          base_url_default?: string
          api_key_refs?: string[]
          speech_only?: boolean
        }
      >
    >('/models/providers/list', {
      signal: options?.signal,
    }),

  // Get list of provider names only
  getProviderNames: (options?: { signal?: AbortSignal }) =>
    request<
      Array<{
        name: string
        display_name: string
        icon?: string
      }>
    >('/models/providers', {
      signal: options?.signal,
    }),

  // Get models for a specific provider
  getProviderModels: (provider: string, options?: { signal?: AbortSignal }) =>
    request<{
      provider: string
      display_name: string
      icon?: string
      description?: string
      model_templates: Array<{
        model_id: string
        display_name: string
        default_params?: Record<string, unknown>
      }>
      api_key_refs?: string[]
      api_key_ref_required: boolean
      base_url_required: boolean
      base_url_default?: string
      supports_custom_models?: boolean
    }>(`/models/providers/${encodeURIComponent(provider)}/models`, {
      signal: options?.signal,
    }),

  // Discover Databricks Model Serving endpoints in a workspace
  discoverDatabricksEndpoints: (
    data: {
      base_url?: string
      workspace_host?: string
      api_key_ref?: string
      iam_credential_id?: string
      oauth_scope?: string
    },
    options?: { signal?: AbortSignal }
  ) =>
    request<{
      endpoints: Array<{
        model_id: string
        display_name: string
        model_type: 'llm' | 'embedding'
        endpoint_kind: 'foundation_model' | 'external_model' | 'custom'
        state?: string | null
        supports_reasoning: boolean
      }>
    }>('/models/providers/databricks/discover', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  // Discover AI Hub / Azure APIM models and deployments
  discoverAiHubModels: (
    data: {
      base_url: string
      api_key_ref?: string
      discovery_path?: string
      api_version?: string
    },
    options?: { signal?: AbortSignal }
  ) =>
    request<{
      models: Array<{
        model_id: string
        display_name: string
        model_type: string
        completion_url?: string | null
        discovery_source: string
        url_style: string
        param_profile: string
        supports_reasoning: boolean
      }>
    }>('/models/providers/aihub/discover', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  // Discover Google Gemini / Generative Language models
  discoverGoogleModels: (
    data: {
      base_url?: string
      api_key_ref?: string
    },
    options?: { signal?: AbortSignal }
  ) =>
    request<{
      models: Array<{
        model_id: string
        display_name: string
        model_type: string
        description?: string | null
        supports_reasoning: boolean
      }>
    }>('/models/providers/google/discover', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  // Discover AWS Bedrock foundation models + inference profiles
  discoverBedrockModels: (
    data: {
      region?: string
      api_key_ref?: string
      aws_secret_access_key_ref?: string
      aws_session_token_ref?: string
      base_url?: string
    },
    options?: { signal?: AbortSignal }
  ) =>
    request<{
      models: Array<{
        model_id: string
        display_name: string
        model_type: string
        provider_name?: string | null
        region: string
        source: string
        supports_reasoning: boolean
      }>
    }>('/models/providers/bedrock/discover', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  // Test model connection (before saving)
  testConnection: (
    data: ModelCreate,
    options?: ModelsRequestOptions
  ) =>
    request<{ status: string; message: string; response?: string; model?: string }>(
      '/models/test',
      {
        method: 'POST',
        body: JSON.stringify(data),
        signal: options?.signal,
        tenantId: options?.tenantId,
        // Provider credential failures may return HTTP 401 — must not log the user out.
        retryOn401: options?.retryOn401 ?? false,
      }
    ),

  // Test model connection (after saving)
  test: (id: string, options?: ModelsRequestOptions) =>
    request<{
      model_id: string
      status: string
      message: string
      response?: string
      model?: string
    }>(`/models/${id}/test`, {
      method: 'POST',
      signal: options?.signal,
      tenantId: options?.tenantId,
      // Azure/OpenAI subscription-key failures surface as HTTP 401 from this endpoint.
      // That is downstream provider auth, not TAGP session expiry.
      retryOn401: options?.retryOn401 ?? false,
    }),

  // Get the default model
  getDefault: (options?: { signal?: AbortSignal }) =>
    request<Model | null>('/models/default', {
      signal: options?.signal,
    }),

  // Set a model as the default
  setDefault: (id: string, options?: ModelsRequestOptions) =>
    request<Model>(`/models/${id}/set-default`, {
      method: 'POST',
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  // Access statistics
  getAccessStatistics: (id: string) => projectsApi.getModelAccessStatistics(id),

  // Access requests (for admins)
  listAccessRequests: (
    id: string,
    params?: { status?: string; page?: number; page_size?: number }
  ) => projectsApi.listModelAccessRequests(id, params),

  approveAccess: (id: string, requestId: string, action: 'approve' | 'reject') =>
    projectsApi.approveModelAccess(id, requestId, action),

  revokeAccess: (id: string, requestId: string) => projectsApi.revokeModelAccess(id, requestId),

  // Native registered reranker models (Phase 5 RAG Enhancement)
  listRerankers: (options?: { project_id?: string; signal?: AbortSignal }) =>
    modelsApi.listAll({
      model_type: 'reranker',
      project_id: options?.project_id,
      signal: options?.signal,
    }),

  /** LLM models eligible for LLM-as-reranker scoring (paired with listRerankers in UI). */
  listLlmRerankCandidates: (options?: { project_id?: string; signal?: AbortSignal }) =>
    modelsApi.listAll({
      model_type: 'llm',
      project_id: options?.project_id,
      signal: options?.signal,
    }),

  getPricing: (
    modelId: string,
    params?: {
      include_history?: boolean
      history_search?: string
      history_page?: number
      history_page_size?: number
      signal?: AbortSignal
    }
  ) => {
    const { signal, include_history, history_search, history_page, history_page_size } = params || {}
    const q = buildQueryString({
      include_history: include_history === true ? true : undefined,
      history_search: history_search || undefined,
      history_page: history_page,
      history_page_size: history_page_size,
    })
    return request<FoundationModelPricingListResponse>(`/models/${modelId}/pricing${q}`, {
      signal,
    })
  },

  pricingProviderRank: (params?: {
    search?: string
    provider?: string
    model_type?: string
    project_id?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: ProviderPricingRankItem[] }>(`/models/pricing/provider-rank${query}`, {
      signal,
    })
  },

  triggerPricingHarvest: (options?: { signal?: AbortSignal }) =>
    request<PricingHarvestResponse>('/models/pricing/harvest', {
      method: 'POST',
      signal: options?.signal,
    }),

  createPricingInterval: (
    modelId: string,
    body: {
      effective_from: string
      effective_until?: string | null
      input_usd_per_million: string | number
      output_usd_per_million: string | number
      pricing_unit?: string
    },
    options?: { signal?: AbortSignal }
  ) =>
    request<FoundationModelPricingInterval>(`/models/${modelId}/pricing/intervals`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal: options?.signal,
    }),

  updatePricingInterval: (
    modelId: string,
    intervalId: string,
    body: Partial<{
      effective_from: string
      effective_until: string | null
      input_usd_per_million: string | number
      output_usd_per_million: string | number
      pricing_unit: string
    }>,
    options?: { signal?: AbortSignal }
  ) =>
    request<FoundationModelPricingInterval>(`/models/${modelId}/pricing/intervals/${intervalId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      signal: options?.signal,
    }),

  deletePricingInterval: (modelId: string, intervalId: string, options?: { signal?: AbortSignal }) =>
    request<void>(`/models/${modelId}/pricing/intervals/${intervalId}`, {
      method: 'DELETE',
      signal: options?.signal,
    }),
}
